import { NavigationProp, useNavigation } from "@react-navigation/native";
import React, { useEffect, useRef, useState } from "react";
import { BaseNavigatorParamList } from "../../../navigators/BaseNavigator";
import { ScrollView, View, YStack, Text } from "tamagui";
import { Platform, Pressable } from "react-native";
import { customColorTokens } from "../../../../tamagui.config";
import * as Clipboard from "expo-clipboard";
import { useToast } from "../../../hooks/useToast";
import { SafeAreaView } from "react-native-safe-area-context";
import { useAtomValue } from "jotai";
import { currentUserAtom } from "../../../../atoms/global";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Loader } from "../../../components/design-system/Loader";
import { useApiQuery } from "../../../../lib/api";
import { Attendance, Event } from "../../../types/event";

// Components
import { EventDetailHeader } from "./components/EventDetailHeader";
import { EventDetailTitle } from "./components/EventDetailTitle";
import { EventDetailHero } from "./components/EventDetailHero";
import { EventDetailInfo } from "./components/EventDetailInfo";
import { EventDetailRoutePills } from "./components/EventDetailRoutePills";
import { EventDetailAttendeesPreview } from "./components/EventDetailAttendeesPreview";
import { EventDetailDisclaimer } from "./components/EventDetailDisclaimer";
import { EventDetailActions } from "./components/EventDetailActions";
import { EventDetailAttendeesModal } from "./components/EventDetailAttendeesModal";
import { TableManners } from "./components/TableManners";
import { EventDetailComments } from "./components/EventDetailComments";
import Avatar from "../../../components/Avatar";
import { Role } from "../../../features/Profile/types";

type EventDetailProps = NativeStackScreenProps<
  BaseNavigatorParamList,
  "EventDetail"
>;

export const EventDetail = ({
  route: {
    params: { eventId, autoOpenShare },
  },
}: EventDetailProps) => {
  const toast = useToast();
  const {
    data: eventData,
    isLoading: isFetchingEvent,
    refetch: fetchEvent,
  } = useApiQuery<{ event: Event }>(
    ["events", eventId],
    `/api/v1/events/${eventId}`,
    {
      refetchOnMount: "always",
    }
  );

  const event = eventData?.event;
  const currentUser = useAtomValue(currentUserAtom);
  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();

  const [isAttendeesModalOpen, setIsAttendeesModalOpen] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  const host = event?.host;
  const route = event?.route;
  const isClubRide = event?.type === "ClubRide";

  const isCurrentUserHost = event?.host_id === currentUser?.id;

  const eventUrl = `https://rides.breakfastclubatx.com/events/${event?.id}`;

  const copyUrl = async () => {
    if (Platform.OS === "web") {
      navigator.clipboard.writeText(eventUrl);
    } else {
      await Clipboard.setUrlAsync(eventUrl);
    }
    toast({
      title: "Link copied to clipboard",
      haptic: "success",
    });
  };

  const handleClose = () => {
    navigation.canGoBack() ? navigation.goBack() : navigation.navigate("Home");
  };

  const handleEdit = () => {
    navigation.navigate("EditEvent", { eventId: event?.id ?? "" });
  };

  const handleShare = () => {
    navigation.navigate("ShareEvent", { eventId });
  };

  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (autoOpenShare && event && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      handleShare();
    }
  }, [autoOpenShare, event]);

  const handleHostPress = (userId: string) => {
    navigation.navigate("MemberProfile", { id: userId });
  };

  const attendees: Attendance[] =
    event?.attendees?.map(
      (a: {
        profile: {
          id: string;
          name: string;
          avatar_url: string | null;
          roles?: { role: string }[];
        };
        id: string;
      }) => ({
        user_id: a.profile.id,
        id: a.id,
        role: (a.profile.roles?.[0]?.role ?? "Member") as Attendance["role"],
        name: a.profile.name,
        avatar_url: a.profile.avatar_url,
      })
    ) ?? [];

  const canShare = event?.visibility !== "Private" || isCurrentUserHost;

  return (
    <View flex={1} bg="$brandParchment" position="relative">
      {isFetchingEvent || isSubmittingComment ? <Loader /> : null}
      <SafeAreaView
        edges={["top"]}
        style={{
          backgroundColor: customColorTokens.brandParchment,
          flex: 1,
        }}
      >
        <ScrollView
          contentContainerStyle={{
            backgroundColor: customColorTokens.brandParchment,
            paddingBottom: 140,
          }}
          flex={1}
          keyboardDismissMode="interactive"
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets
          showsVerticalScrollIndicator={false}
        >
          <View
            bg="$brandParchment"
            w="100%"
            h={isClubRide ? "auto" : 300}
            alignItems="center"
            pos="relative"
            borderBottomWidth={isClubRide ? 0 : 1}
          >
            <EventDetailHeader
              onClose={handleClose}
              onEdit={handleEdit}
              onShare={handleShare}
              isCurrentUserHost={isCurrentUserHost}
              canShare={canShare}
            />

            {event ? (
              <>
                <EventDetailTitle title={event.name ?? ""} />
                <EventDetailHero route={route} eventType={event.type} />
              </>
            ) : null}
          </View>

          {event ? (
            <>
              <EventDetailInfo
                date={event.date}
                time={event.time}
                location={event.location}
                visibility={event.visibility}
                eventType={event.type}
                host={host}
                onHostPress={handleHostPress}
                description={event.description}
              />

              <EventDetailRoutePills
                route={route}
                groups={event.groups}
                terrain={event.terrain}
                visibility={event.visibility}
                isClubRide={isClubRide}
              />

              {host ? (
                <YStack gap="$1" px="$4" mt="$4">
                  <Text
                    fontFamily="$heading"
                    fontSize={16}
                    textTransform="uppercase"
                  >
                    Hosted by
                  </Text>
                  <Pressable onPress={() => handleHostPress(host.user_id)}>
                    <YStack>
                      <Avatar
                        size={50}
                        url={host.avatar_url ?? null}
                        imageStyle={{ borderRadius: 999 }}
                        role={host.role as Role}
                      />
                      <Text mt="$2">{host.name}</Text>
                    </YStack>
                  </Pressable>
                </YStack>
              ) : null}

              <EventDetailAttendeesPreview
                attendees={attendees}
                onPress={() => setIsAttendeesModalOpen(true)}
              />

              <EventDetailComments
                eventId={event.id ?? ""}
                onCommentSubmitting={setIsSubmittingComment}
              />

              <EventDetailDisclaimer isClubRide={isClubRide} />

              <TableManners />
            </>
          ) : null}
        </ScrollView>

        {event ? (
          <EventDetailActions
            copyUrl={copyUrl}
            refreshQuery={() => fetchEvent()}
            event={event}
          />
        ) : null}
      </SafeAreaView>

      {event ? (
        <EventDetailAttendeesModal
          isOpen={isAttendeesModalOpen}
          onClose={() => setIsAttendeesModalOpen(false)}
          attendees={attendees}
        />
      ) : null}
    </View>
  );
};
